import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { V2Alert, V2Button, V2FormField, V2FormGrid, V2Modal } from '@sakhtar/design-system/react';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { fetchNextCode } from '../../lib/suggestedCode.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { citiesOfProvince, IRAN_PROVINCES } from '../../lib/iranGeography.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { LabelField } from './LabelField.js';
import {
  buildCustomerPayload,
  changeCustomerKind,
  emptyCustomerForm,
  MAX_PHONE_ROWS,
  PHONE_KIND_LABEL,
  validateCustomerForm,
  type CustomerFormKind,
  type CustomerFormState,
  type PhoneKind,
} from './customerForm.js';

interface FormOptions {
  readonly owners: readonly {
    readonly memberRef: string | null;
    readonly name: string;
    readonly isMe: boolean;
  }[];
}

export interface CustomerCreateDialogProps {
  readonly open: boolean;
  readonly base: string;
  readonly onClose: () => void;
  readonly onCreated: (ref: string | undefined) => void;
  /** Values the form starts with each time it opens (e.g. the caller's number). */
  readonly initial?: Partial<CustomerFormState> | undefined;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h3
        style={{
          margin: 0,
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--sk-fg-muted)',
          paddingBottom: 6,
          borderBottom: '1px solid var(--sk-border)',
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Persian message for a server field path; unknown paths fall back to the generic text. */
function serverFieldMessage(path: string): string {
  if (path.startsWith('extraPhones')) return 'شماره تلفن نامعتبر است.';
  if (path === 'contactPerson.mobile') return 'موبایل شخص در ارتباط نامعتبر است.';
  const known: Record<string, string> = {
    nationalId: 'شناسه یا کد ملی معتبر نیست.',
    economicCode: 'کد اقتصادی معتبر نیست.',
    registrationNumber: 'شماره ثبت معتبر نیست.',
    postalCode: 'کد پستی معتبر نیست.',
    mobile: 'شماره موبایل معتبر نیست.',
    phone: 'تلفن ثابت معتبر نیست.',
    ownerMemberRef: 'کارشناس فروش انتخاب‌شده معتبر نیست.',
    labelIds: 'برچسب انتخاب‌شده معتبر نیست یا غیرفعال شده است.',
    name: 'نام معتبر نیست.',
    code: 'کد مشتری معتبر نیست.',
  };
  return known[path] ?? 'مقدار این فیلد معتبر نیست.';
}

/** UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — CRM; V2 modal and form-grid pattern. */
export function CustomerCreateDialog({
  open,
  base,
  onClose,
  onCreated,
  initial,
}: CustomerCreateDialogProps) {
  const [form, setForm] = useState<CustomerFormState>(() => emptyCustomerForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nextRowId, setNextRowId] = useState(1);
  const options = useApiGet<FormOptions>(open ? `${base}/api/v1/crm/customers/options` : null, [
    open,
    base,
  ]);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyCustomerForm(), ...initial });
    setErrors({});
    setBanner(null);
    setNextRowId(1);
    // The code field is never left empty: it opens with the suggested next code.
    let cancelled = false;
    void fetchNextCode(base, 'party').then((code) => {
      if (!cancelled && code)
        setForm((current) => (current.code === '' ? { ...current, code } : current));
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const legal = form.kind === 'organization';
  const cities = useMemo(() => citiesOfProvince(form.province), [form.province]);
  const set = <K extends keyof CustomerFormState>(key: K, value: CustomerFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!(key in current)) return current;
      const rest = { ...current };
      delete rest[key];
      return rest;
    });
  };
  const setKind = (kind: CustomerFormKind) => {
    setForm((current) => changeCustomerKind(current, kind));
    setErrors({});
  };
  const updatePhone = (
    id: number,
    patch: Partial<{ kind: PhoneKind; number: string; label: string }>,
  ) =>
    setForm((current) => ({
      ...current,
      phones: current.phones.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const problems = validateCustomerForm(form);
    setErrors(problems);
    setBanner(null);
    if (Object.keys(problems).length > 0) return;
    setBusy(true);
    try {
      const created = await apiPost<{ ref?: string }>(
        `${base}/api/v1/crm/customers`,
        buildCustomerPayload(form),
      );
      onCreated(created.ref);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422) {
        const fields: Record<string, string> = {};
        for (const detail of caught.envelope.error.details ?? [])
          fields[detail.path] = serverFieldMessage(detail.path);
        if (Object.keys(fields).length > 0) {
          setErrors(fields);
          setBanner('برخی ورودی‌ها معتبر نیستند. موارد مشخص‌شده را اصلاح کنید.');
          return;
        }
      }
      if (caught instanceof ApiError && caught.status === 409) {
        // Someone took the suggested code meanwhile (or the typed one exists): propose a fresh one.
        const fresh = await fetchNextCode(base, 'party');
        if (fresh && fresh !== form.code) set('code', fresh);
        setBanner('کد مشتری تکراری بود؛ کد جدیدی پیشنهاد شد. بررسی کنید و دوباره ثبت کنید.');
        return;
      }
      setBanner(bannerMessageFrom(caught, 'ثبت مشتری ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  const owners = options.status === 'ready' ? options.data.owners : [];
  const radio = (kind: CustomerFormKind, label: string) => (
    <label
      key={kind}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 'var(--sk-r-md)',
        border: `1px solid ${form.kind === kind ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
        background:
          form.kind === kind
            ? 'color-mix(in oklch, var(--sk-brand-500), transparent 92%)'
            : 'var(--sk-bg-elev)',
        cursor: 'pointer',
        fontSize: 13,
      }}
    >
      <input
        type="radio"
        className="sk-radio"
        name="customer-kind"
        checked={form.kind === kind}
        onChange={() => setKind(kind)}
      />
      {label}
    </label>
  );

  const geography = (
    <V2FormGrid>
      <V2FormField label="استان" width="medium" error={errors['province']}>
        <select
          className="sk-select"
          value={form.province}
          onChange={(event) => {
            set('province', event.target.value);
            set('city', '');
          }}
        >
          <option value="">انتخاب استان</option>
          {IRAN_PROVINCES.map((province) => (
            <option key={province.name} value={province.name}>
              {province.name}
            </option>
          ))}
        </select>
      </V2FormField>
      <V2FormField label="شهر" width="medium" error={errors['city']}>
        <select
          className="sk-select"
          value={form.city}
          disabled={form.province === ''}
          onChange={(event) => set('city', event.target.value)}
        >
          <option value="">
            {form.province === '' ? 'ابتدا استان را انتخاب کنید' : 'انتخاب شهر'}
          </option>
          {cities.map((city) => (
            <option key={city} value={city}>
              {city}
            </option>
          ))}
        </select>
      </V2FormField>
      <V2FormField label="کد پستی" width="medium" hint="۱۰ رقم" error={errors['postalCode']}>
        <input
          className="sk-input ltr sk-num"
          inputMode="numeric"
          maxLength={10}
          value={form.postalCode}
          onChange={(event) => set('postalCode', event.target.value)}
        />
      </V2FormField>
      <V2FormField
        label={legal ? 'نشانی دفتر مرکزی' : 'نشانی دقیق پستی'}
        width="full"
        error={errors['address']}
      >
        <textarea
          className="sk-textarea"
          rows={3}
          value={form.address}
          onChange={(event) => set('address', event.target.value)}
        />
      </V2FormField>
      <V2FormField
        label={legal ? 'کارشناس فروش / بازاریاب' : 'بازاریاب / عامل فروش'}
        width="medium"
        error={errors['ownerMemberRef']}
        hint={options.status === 'error' ? 'فهرست کارشناسان بارگذاری نشد.' : undefined}
      >
        <select
          className="sk-select"
          value={form.ownerMemberRef}
          disabled={options.status !== 'ready'}
          onChange={(event) => set('ownerMemberRef', event.target.value)}
        >
          <option value="">
            {options.status === 'loading' ? 'در حال بارگذاری…' : 'بدون کارشناس'}
          </option>
          {owners.map((owner) =>
            owner.memberRef ? (
              <option key={owner.memberRef} value={owner.memberRef}>
                {owner.name}
                {owner.isMe ? ' (من)' : ''}
              </option>
            ) : null,
          )}
        </select>
      </V2FormField>
      <V2FormField
        label="برچسب‌ها"
        width="wide"
        hint="از برچسب‌های موجود انتخاب کن یا برچسب تازه بساز؛ برای دسته‌بندی و جست‌وجوی سریع مشتری‌ها."
        error={errors['labelIds']}
      >
        <LabelField
          base={base}
          value={form.labelIds}
          onChange={(labelIds) => set('labelIds', labelIds)}
          canCreate
        />
      </V2FormField>
    </V2FormGrid>
  );

  return (
    <V2Modal
      open={open}
      onClose={onClose}
      dismissible={!busy}
      size="lg"
      title="ثبت مشتری جدید"
      footer={
        <>
          <V2Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            انصراف
          </V2Button>
          <V2Button
            type="submit"
            form="customer-create-form"
            variant="primary"
            icon="plus"
            disabled={busy}
          >
            {busy ? 'در حال ثبت…' : 'ذخیره مشتری جدید'}
          </V2Button>
        </>
      }
    >
      <form
        id="customer-create-form"
        noValidate
        onSubmit={submit}
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        {banner ? <V2Alert tone="danger">{banner}</V2Alert> : null}
        <V2Alert tone="info">
          برای شروع، نام و نوع مشتری کافی است؛ کد به‌صورت خودکار پیشنهاد می‌شود. اطلاعات دیگر را
          می‌توانید بعداً تکمیل کنید. بیمارستان را با نام خودش و نوع «شخص حقوقی» ثبت کنید. تغییر نوع
          مشتری اطلاعات واردشده را پاک نمی‌کند؛ شناسهٔ متناسب با نوع را بررسی کنید.
        </V2Alert>
        <div
          role="radiogroup"
          aria-label="نوع مشتری"
          style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}
        >
          <span style={{ alignSelf: 'center', fontSize: 13, fontWeight: 600 }}>نوع مشتری</span>
          {radio('person', 'شخص حقیقی')}
          {radio('organization', 'شخص حقوقی')}
        </div>

        {legal ? (
          <>
            <Section title="مشخصات ثبتی و شرکتی">
              <V2FormGrid>
                <V2FormField
                  label="کد مشتری"
                  width="short"
                  hint="پیشنهاد خودکار؛ قابل تغییر"
                  error={errors['code']}
                >
                  <input
                    className="sk-input ltr"
                    value={form.code}
                    onChange={(event) => set('code', event.target.value)}
                  />
                </V2FormField>
                <V2FormField label="نام شرکت" required width="wide" error={errors['name']}>
                  <input
                    className="sk-input"
                    value={form.name}
                    onChange={(event) => set('name', event.target.value)}
                  />
                </V2FormField>
                <V2FormField
                  label="شناسه ملی"
                  width="medium"
                  hint="۱۱ رقم"
                  error={errors['nationalId']}
                >
                  <input
                    className="sk-input ltr sk-num"
                    inputMode="numeric"
                    maxLength={11}
                    value={form.nationalId}
                    onChange={(event) => set('nationalId', event.target.value)}
                  />
                </V2FormField>
                <V2FormField
                  label="کد اقتصادی"
                  width="medium"
                  hint="شناسهٔ ملی معتبر ۱۱ رقمی یا کد اقتصادی قدیمی ۱۲ رقمی"
                  error={errors['economicCode']}
                >
                  <input
                    className="sk-input ltr sk-num"
                    inputMode="numeric"
                    maxLength={12}
                    value={form.economicCode}
                    onChange={(event) => set('economicCode', event.target.value)}
                  />
                </V2FormField>
                <V2FormField label="شماره ثبت" width="medium" error={errors['registrationNumber']}>
                  <input
                    className="sk-input ltr sk-num"
                    inputMode="numeric"
                    value={form.registrationNumber}
                    onChange={(event) => set('registrationNumber', event.target.value)}
                  />
                </V2FormField>
              </V2FormGrid>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>تلفن‌های شرکت / شخص در ارتباط</div>
                {form.phones.map((row, index) => (
                  <div
                    key={row.id}
                    style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}
                  >
                    <select
                      className="sk-select"
                      aria-label="نوع تلفن"
                      style={{ width: 130 }}
                      value={row.kind}
                      onChange={(event) =>
                        updatePhone(row.id, { kind: event.target.value as PhoneKind })
                      }
                    >
                      {(Object.keys(PHONE_KIND_LABEL) as PhoneKind[]).map((kind) => (
                        <option key={kind} value={kind}>
                          {PHONE_KIND_LABEL[kind]}
                        </option>
                      ))}
                    </select>
                    <div style={{ flex: '1 1 180px', minWidth: 160 }}>
                      <input
                        className="sk-input ltr sk-num"
                        aria-label={`شماره تلفن ${index + 1}`}
                        inputMode="tel"
                        placeholder={row.kind === 'mobile' ? '09121234567' : '02112345678'}
                        value={row.number}
                        onChange={(event) => {
                          updatePhone(row.id, { number: event.target.value });
                          setErrors((current) => {
                            const rest = { ...current };
                            delete rest[`extraPhones.${index}.number`];
                            return rest;
                          });
                        }}
                      />
                      {errors[`extraPhones.${index}.number`] ? (
                        <div
                          role="alert"
                          style={{ color: 'var(--sk-danger)', fontSize: 12, marginTop: 4 }}
                        >
                          {errors[`extraPhones.${index}.number`]}
                        </div>
                      ) : null}
                    </div>
                    <input
                      className="sk-input"
                      aria-label="توضیح تلفن"
                      placeholder="توضیح (اختیاری)"
                      style={{ flex: '1 1 140px', minWidth: 120 }}
                      value={row.label}
                      onChange={(event) => updatePhone(row.id, { label: event.target.value })}
                    />
                    <V2Button
                      type="button"
                      variant="ghost"
                      icon="x"
                      iconOnly
                      aria-label="حذف شماره"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          phones: current.phones.filter((item) => item.id !== row.id),
                        }))
                      }
                    />
                  </div>
                ))}
                <div>
                  <V2Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon="plus"
                    disabled={form.phones.length >= MAX_PHONE_ROWS}
                    onClick={() => {
                      setForm((current) => ({
                        ...current,
                        phones: [
                          ...current.phones,
                          { id: nextRowId, kind: 'landline', number: '', label: '' },
                        ],
                      }));
                      setNextRowId((value) => value + 1);
                    }}
                  >
                    افزودن شماره تلفن
                  </V2Button>
                </div>
              </div>
            </Section>

            <Section title="اطلاعات شخص در ارتباط">
              <V2FormGrid>
                <V2FormField
                  label="نام و نام خانوادگی رابط"
                  width="medium"
                  error={errors['contactPerson.fullName']}
                >
                  <input
                    className="sk-input"
                    value={form.contactName}
                    onChange={(event) => set('contactName', event.target.value)}
                  />
                </V2FormField>
                <V2FormField label="سمت / موقعیت شغلی" width="medium">
                  <input
                    className="sk-input"
                    value={form.contactTitle}
                    onChange={(event) => set('contactTitle', event.target.value)}
                  />
                </V2FormField>
                <V2FormField
                  label="شماره موبایل مستقیم"
                  width="medium"
                  error={errors['contactPerson.mobile']}
                >
                  <input
                    className="sk-input ltr sk-num"
                    inputMode="tel"
                    value={form.contactMobile}
                    onChange={(event) => set('contactMobile', event.target.value)}
                  />
                </V2FormField>
              </V2FormGrid>
            </Section>

            <Section title="اطلاعات مکانی">{geography}</Section>
          </>
        ) : (
          <Section title="اطلاعات مشتری">
            <V2FormGrid>
              <V2FormField
                label="کد مشتری"
                width="short"
                hint="پیشنهاد خودکار؛ قابل تغییر"
                error={errors['code']}
              >
                <input
                  className="sk-input ltr"
                  value={form.code}
                  onChange={(event) => set('code', event.target.value)}
                />
              </V2FormField>
              <V2FormField label="نام و نام خانوادگی" required width="wide" error={errors['name']}>
                <input
                  className="sk-input"
                  value={form.name}
                  onChange={(event) => set('name', event.target.value)}
                />
              </V2FormField>
              <V2FormField label="کد ملی" width="medium" hint="۱۰ رقم" error={errors['nationalId']}>
                <input
                  className="sk-input ltr sk-num"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.nationalId}
                  onChange={(event) => set('nationalId', event.target.value)}
                />
              </V2FormField>
              <V2FormField label="شماره موبایل" width="medium" error={errors['mobile']}>
                <input
                  className="sk-input ltr sk-num"
                  inputMode="tel"
                  placeholder="09121234567"
                  value={form.mobile}
                  onChange={(event) => set('mobile', event.target.value)}
                />
              </V2FormField>
              <V2FormField label="تلفن ثابت" width="medium" error={errors['phone']}>
                <input
                  className="sk-input ltr sk-num"
                  inputMode="tel"
                  placeholder="02112345678"
                  value={form.phone}
                  onChange={(event) => set('phone', event.target.value)}
                />
              </V2FormField>
            </V2FormGrid>
            {geography}
          </Section>
        )}
      </form>
    </V2Modal>
  );
}
